/**
 * Shop search (redesign 2026-10). Replaces the theme's predictive search with a
 * small client-side search over our own index (snippets/search-index-json.liquid):
 * products, Parfüm-Blog articles and collections. The shop is small enough to
 * search everything in the browser, which gives us what Shopify's search can't:
 *   - typo tolerance ("savage", "bleu de chanle"), umlaut folding (dufte / düfte / duefte),
 *   - synonyms ("männer" → Herren, "süß" → Gourmand, "lv" → Louis Vuitton),
 *   - compound splitting ("Sommerduft" → Sommer),
 *   - one ranked list with every hit labelled Duft / Set / Journal / Kategorie.
 *
 * Elements:
 *   <pp-search mode="modal|inline" prefer="journal">  live results under an input
 *   <pp-search-page>                                  ranks the server-rendered cards on /search
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const INDEX_URL = `${ROOT}collections/all?view=search-index`;
const SEARCH_URL = `${ROOT}search`;

/* ---------------------------------------------------------------- text */

/** Lowercase, strip accents, fold umlauts both ways (ü = ue = u), keep a–z/0–9. */
function fold(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function tokens(value) {
  const text = fold(value);
  return text ? text.split(' ') : [];
}

const STOP_WORDS = new Set(
  'der die das den dem des ein eine einen und oder mit fur von zu im in am an auf ist wie was wo welche welcher welches ich mir mich du dich fuer bei als auch nach probe proben duftprobe duftproben parfum parfums parfu duft dufte ml de la le'
    .split(' ')
    .map(fold)
);

/** Each group: words a shopper might type → words that occur in our data. */
const SYNONYM_GROUPS = [
  ['herren', 'herrendufte', 'herr', 'mann', 'manner', 'mannlich', 'maskulin', 'men', 'man', 'male', 'him', 'ihn', 'freund'],
  ['damen', 'damendufte', 'dame', 'frau', 'frauen', 'weiblich', 'feminin', 'women', 'woman', 'female', 'her', 'sie', 'freundin'],
  ['unisex', 'beide', 'neutral'],
  ['sommer', 'summer', 'sommerlich', 'urlaub', 'hitze'],
  ['winter', 'winterlich', 'kalt'],
  ['herbst', 'autumn', 'fall'],
  ['fruhling', 'fruhjahr', 'spring'],
  ['ganzjahrig', 'alltag', 'allround', 'ganzes jahr'],
  ['buro', 'office', 'arbeit', 'job', 'business'],
  ['date', 'abend', 'ausgehen', 'nacht', 'party', 'romantisch', 'verfuhrerisch'],
  ['klassiker', 'klassisch', 'zeitlos'],
  ['frisch', 'fresh', 'aquatisch', 'meer', 'meeresbrise', 'zitrus', 'citrus', 'zitrone', 'bergamotte', 'sauber'],
  ['gourmand', 'suss', 'sus', 'sweet', 'vanille', 'vanilla', 'karamell', 'fruchtig', 'frucht', 'kirsche'],
  ['holzig', 'wurzig', 'holz', 'woody', 'spicy', 'oud', 'leder', 'zeder', 'sandelholz', 'tabak'],
  ['blumig', 'floral', 'blumen', 'rose', 'jasmin', 'blute'],
  ['orientalisch', 'oriental', 'amber', 'ambra', 'weihrauch', 'harz'],
  ['sale', 'rabatt', 'angebot', 'angebote', 'reduziert', 'gunstig', 'billig', 'deal', 'prozente'],
  ['set', 'sets', 'bundle', 'bundles', 'paket', 'probenset', 'entdeckerset', 'geschenkset', 'geschenk', 'kennenlernset'],
  ['louis', 'vuitton', 'lv'],
  ['marly', 'pdm'],
  ['tom', 'ford', 'tomford', 'tf'],
  ['bestseller', 'beliebt', 'beliebteste', 'meistverkauft', 'top'],
  ['neuheiten', 'neu', 'neuheit', 'new'],
  ['haltbarkeit', 'halt', 'haltbar', 'langanhaltend', 'longevity', 'sillage', 'lange'],
].map((group) => group.map(fold));

const SYNONYMS = new Map();
for (const group of SYNONYM_GROUPS) {
  for (const word of group) SYNONYMS.set(word, group);
}

/** Damerau–Levenshtein distance with an early exit above `max`. */
function distance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const rows = [];
  for (let i = 0; i <= a.length; i++) {
    rows[i] = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      if (i === 0) {
        rows[i][j] = j;
        continue;
      }
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, rows[i - 2][j - 2] + 1);
      }
      rows[i][j] = value;
      if (value < rowMin) rowMin = value;
    }
    if (i > 0 && rowMin > max) return max + 1;
  }
  return rows[a.length][b.length];
}

/**
 * How well a query word matches one indexed word, 0 … 1.
 * `strict` (used for synonyms): whole words only, so "men" or "her" can't match "mens" or "herbst".
 */
function wordScore(query, word, strict = false) {
  // 1–2 typed letters: only the start of real words ("d" → Dior, Delina; not "de", "die").
  if (query.length <= 2) {
    if (word.length < 3 || STOP_WORDS.has(word)) return word === query && word.length > 1 ? 1 : 0;
    return word.startsWith(query) ? 0.9 : 0;
  }
  if (word === query) return 1;
  if (strict) return query.length >= 5 && word.startsWith(query) ? 0.8 : 0;
  if (query.length >= 2 && word.startsWith(query)) return 0.9;
  if (query.length >= 4 && word.includes(query)) return 0.7;
  if (query.length >= 4) {
    const max = query.length >= 8 ? 2 : 1;
    const full = distance(query, word, max);
    // Also compare against the start of longer words, so a half-typed word with a typo still hits.
    const start = word.length > query.length ? distance(query, word.slice(0, query.length), max) : full;
    const best = Math.min(full, start);
    if (best > max) return 0;
    if (best === 1) return 0.75;
    if (best === 2) return 0.55;
  }
  return 0;
}

/* --------------------------------------------------------------- index */

const FIELD_WEIGHTS = { n: 6, b: 5, c: 3.5, g: 3.5, no: 3, inc: 2.5, tg: 3, k: 1.5, x: 1.2, d: 0.8 };

function prepareDoc(doc, type, fields) {
  doc.type = type;
  doc.fields = {};
  for (const key of fields) {
    const value = Array.isArray(doc[key]) ? doc[key].join(' ') : doc[key];
    let words = tokens(value);
    if (key === 'd' || key === 'x') words = words.filter((word) => word.length >= 4);
    doc.fields[key] = [...new Set(words)];
  }
  doc.phrase = fold(`${doc.b || ''} ${doc.n || ''}`);
  return doc;
}

function prepareIndex(raw) {
  const products = (raw.products || []).map((p) => {
    p.key = `p${p.id}`;
    return prepareDoc(p, 'product', ['n', 'b', 'c', 'g', 'no', 'inc', 'k', 'd']);
  });
  const articles = (raw.articles || []).map((a, i) => {
    a.key = `a${i}`;
    return prepareDoc(a, 'article', ['n', 'tg', 'x', 'd']);
  });
  const collections = (raw.collections || []).map((c) => prepareDoc(c, 'collection', ['n']));
  const vocabulary = new Set();
  for (const doc of [...products, ...articles, ...collections]) {
    for (const words of Object.values(doc.fields)) words.forEach((word) => vocabulary.add(word));
  }
  return { products, articles, collections, vocabulary };
}

let indexPromise = null;

export function loadIndex() {
  if (indexPromise) return indexPromise;
  const inline = document.getElementById('pp-search-index');
  if (inline) {
    try {
      indexPromise = Promise.resolve(prepareIndex(JSON.parse(inline.textContent)));
      return indexPromise;
    } catch (error) {
      console.warn('[search] inline index unreadable', error);
    }
  }
  indexPromise = fetch(INDEX_URL, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((text) => prepareIndex(JSON.parse(text)))
    .catch((error) => {
      indexPromise = null;
      throw error;
    });
  return indexPromise;
}

/* -------------------------------------------------------------- search */

/** Splits the query into words; each word carries the alternatives it may match. */
function parseQuery(query, vocabulary) {
  let words = tokens(query);
  const meaningful = words.filter((word) => !STOP_WORDS.has(word));
  if (meaningful.length) words = meaningful;

  const terms = [];
  for (const word of words) {
    let parts = [word];
    // German compounds ("sommerduft", "vanilleduft", "herrenparfum"): split when the word itself is unknown.
    if (word.length >= 7 && !vocabulary.has(word) && !SYNONYMS.has(word)) {
      for (let i = 3; i <= word.length - 3; i++) {
        const left = word.slice(0, i);
        const right = word.slice(i);
        const known = (part) => vocabulary.has(part) || SYNONYMS.has(part);
        if (known(left) || known(right)) {
          parts = [left, right].filter((part) => !STOP_WORDS.has(part) && part.length >= 3);
          if (!parts.length) parts = [word];
          break;
        }
      }
    }
    for (const part of parts) {
      const alternatives = new Map([[part, 1]]);
      const group = SYNONYMS.get(part);
      if (group) group.forEach((alt) => alternatives.has(alt) || alternatives.set(alt, 0.85));
      terms.push(alternatives);
    }
  }
  return terms;
}

/** Fields searched for 1–2 letter queries: names and brands (article titles are names too). */
const SHORT_QUERY_FIELDS = new Set(['n', 'b']);

function scoreTerm(doc, alternatives) {
  let best = 0;
  const short = [...alternatives.keys()].every((alt) => alt.length <= 2);
  for (const [key, words] of Object.entries(doc.fields)) {
    if (short && !SHORT_QUERY_FIELDS.has(key)) continue;
    const weight = FIELD_WEIGHTS[key] || 1;
    for (const [alt, altWeight] of alternatives) {
      for (const word of words) {
        const score = wordScore(alt, word, altWeight < 1) * weight * altWeight;
        if (score > best) best = score;
      }
    }
  }
  return best;
}

const RELEVANCE_FLOOR = 0.3;

function rank(docs, terms, phrase) {
  const hits = [];
  for (const doc of docs) {
    let total = 0;
    let matched = 0;
    for (const alternatives of terms) {
      const score = scoreTerm(doc, alternatives);
      if (score > 0) matched++;
      total += score;
    }
    if (!matched) continue;
    if (phrase && doc.phrase && doc.phrase.includes(phrase)) total += 6;
    hits.push({ doc, total, matched });
  }
  const complete = hits.filter((hit) => hit.matched === terms.length);
  let pool = complete.length ? complete : hits;
  pool.sort((a, b) => b.matched - a.matched || b.total - a.total);
  // Drop hits that only mention a word in passing compared to the best ones.
  const top = pool.length ? pool[0].total : 0;
  pool = pool.filter((hit) => hit.total >= top * RELEVANCE_FLOOR);
  return { list: pool.map((hit) => hit.doc), partial: !complete.length && hits.length > 0 };
}

/**
 * @param {ReturnType<typeof prepareIndex>} index
 * @param {string} query
 */
export function search(index, query) {
  const terms = parseQuery(query, index.vocabulary);
  if (!terms.length) {
    return { products: index.products, articles: index.articles, collections: [], empty: true, partial: false };
  }
  const phrase = fold(query);
  const products = rank(index.products, terms, phrase);
  const articles = rank(index.articles, terms, phrase);
  const collections = rank(index.collections, terms, phrase);
  // A partial (not all words found) list is only worth showing when nothing matches fully.
  const anyComplete = !products.partial && products.list.length
    || !articles.partial && articles.list.length
    || !collections.partial && collections.list.length;
  const pick = (result) => (anyComplete && result.partial ? [] : result.list);
  return {
    products: pick(products),
    articles: pick(articles),
    collections: pick(collections),
    empty: false,
    partial: !anyComplete,
  };
}

/* ----------------------------------------------------------- rendering */

const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

// German shop format (Einstellungen → Währungsformatierung): "7,99 €"
const money = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

const POPULAR = ['Herren', 'Damen', 'Louis Vuitton', 'Sauvage', 'Vanille', 'Frisch', 'Sets'];
const POPULAR_JOURNAL = ['Haltbarkeit', 'Herrendüfte', 'Damendüfte', 'Winter', 'Eau de Parfum', 'Louis Vuitton', 'Dior'];

function productRow(p) {
  const meta = [p.set ? 'Probenset' : p.g, `ab ${money(p.p)}`].filter(Boolean).join(' · ');
  return `<a class="pps__row" href="${escapeHtml(p.u)}" role="option">
    <span class="pps__thumb">${p.img ? `<img src="${escapeHtml(p.img)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
    <span class="pps__txt">
      <span class="pps__eyebrow">${escapeHtml(p.b)}</span>
      <span class="pps__name">${escapeHtml(p.n)}</span>
      <span class="pps__meta">${escapeHtml(meta)}${p.cp ? ` <s>${money(p.cp)}</s>` : ''}</span>
    </span>
    <span class="pps__tag">${p.set ? 'Set' : 'Duft'}</span>
  </a>`;
}

function articleRow(a) {
  const topic = (a.tg || []).find((tag) => tag !== 'Duftwissen') || (a.tg || [])[0] || 'Ratgeber';
  const meta = `${a.m} Min. Lesezeit`;
  return `<a class="pps__row" href="${escapeHtml(a.u)}" role="option">
    <span class="pps__thumb">${a.img ? `<img src="${escapeHtml(a.img)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
    <span class="pps__txt">
      <span class="pps__eyebrow">${escapeHtml(topic)}</span>
      <span class="pps__name pps__name--article">${escapeHtml(a.n)}</span>
      <span class="pps__meta">${escapeHtml(meta)}</span>
    </span>
    <span class="pps__tag pps__tag--journal">Journal</span>
  </a>`;
}

function group(label, count, body, extraClass = '') {
  return `<div class="pps__group ${extraClass}">
    <p class="pps__label">${label}${count != null ? ` <span>${count}</span>` : ''}</p>
    ${body}
  </div>`;
}

function chips(items) {
  return `<div class="pps__chips">${items.join('')}</div>`;
}

function readRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem('viewedProducts') || '[]').map(String);
  } catch {
    return [];
  }
}

/* ------------------------------------------------------- <pp-search> */

class PPSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.input = this.querySelector('[data-pps-input]');
    this.panel = this.querySelector('[data-pps-results]');
    this.form = this.querySelector('form');
    if (!this.input || !this.panel) return;

    this.mode = this.getAttribute('mode') || 'modal';
    this.preferJournal = this.getAttribute('prefer') === 'journal';
    this.lastQuery = null;
    this.timer = 0;

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 90);
    });
    this.input.addEventListener('focus', () => {
      loadIndex().catch(() => {});
      if (this.mode === 'inline') this.update(true);
    });
    this.input.addEventListener('keydown', (event) => this.onKeyDown(event));
    this.panel.addEventListener('click', (event) => this.onPanelClick(event));

    if (this.mode === 'inline') {
      document.addEventListener('click', (event) => {
        if (!event.composedPath().includes(this)) this.closeInline();
      });
    } else {
      // Modal: (re)render the start view whenever the dialog opens.
      const dialogComponent = this.closest('dialog-component');
      dialogComponent?.addEventListener('dialog:open', () => {
        loadIndex()
          .then(() => this.update(true))
          .catch(() => this.renderError());
        requestAnimationFrame(() => this.input.focus());
      });
      this.update(true);
    }
  }

  get query() {
    return this.input.value.trim();
  }

  async update(force = false) {
    const query = this.query;
    if (!force && query === this.lastQuery) return;
    this.lastQuery = query;

    let index;
    try {
      index = await loadIndex();
    } catch {
      this.renderError();
      return;
    }
    if (query !== this.query) return; // a newer keystroke is already on its way

    this.panel.innerHTML = query ? this.renderResults(index, query) : this.renderStart(index);
    this.activeIndex = -1;
    if (this.mode === 'inline') {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
  }

  renderStart(index) {
    const parts = [];
    const popular = this.preferJournal ? POPULAR_JOURNAL : POPULAR;
    parts.push(
      group(
        this.preferJournal ? 'Beliebte Themen' : 'Beliebte Suchen',
        null,
        chips([
          ...popular.map((term) => `<button type="button" class="pps__chip" data-pps-term="${escapeHtml(term)}">${escapeHtml(term)}</button>`),
          `<a class="pps__chip pps__chip--quiz" href="${ROOT}pages/duft-test">Duft-Test machen →</a>`,
        ])
      )
    );

    if (this.preferJournal) {
      const latest = index.articles.slice(0, 3);
      if (latest.length) parts.push(group('Neu im Parfüm-Blog', null, `<div class="pps__list">${latest.map(articleRow).join('')}</div>`));
      return parts.join('');
    }

    const recentIds = readRecentlyViewed();
    const recent = recentIds.map((id) => index.products.find((p) => String(p.id) === id)).filter(Boolean).slice(0, 3);
    if (recent.length) {
      parts.push(group('Zuletzt angesehen', null, `<div class="pps__list">${recent.map(productRow).join('')}</div>`));
    }

    const latest = [...index.articles].slice(0, 2);
    if (latest.length) {
      parts.push(group('Neu im Parfüm-Blog', null, `<div class="pps__list">${latest.map(articleRow).join('')}</div>`));
    }
    return parts.join('');
  }

  renderResults(index, query) {
    const result = search(index, query);
    const limitProducts = this.mode === 'inline' ? 3 : 5;
    const limitArticles = this.mode === 'inline' ? 4 : 3;
    const products = result.products.slice(0, limitProducts);
    const articles = result.articles.slice(0, limitArticles);
    const categories = result.collections.slice(0, 5);
    const total = result.products.length + result.articles.length;

    if (!total && !categories.length) {
      // Don't greet a shopper with "nothing found" after one or two letters.
      if (query.length < 3) return this.renderStart(index);
      const best = index.products.filter((p) => (p.c || []).includes('Bestseller')).slice(0, 3);
      return `<div class="pps__none">
          <p class="pps__none-title">Keine Treffer für „${escapeHtml(query)}“</p>
          <p class="pps__none-text">Versuch es mit einer Marke, einem Duftnamen, einer Duftnote oder einem Thema.</p>
        </div>
        ${group(
          'Beliebte Suchen',
          null,
          chips(POPULAR.map((term) => `<button type="button" class="pps__chip" data-pps-term="${escapeHtml(term)}">${escapeHtml(term)}</button>`))
        )}
        ${best.length ? group('Beliebte Düfte', null, `<div class="pps__list">${best.map(productRow).join('')}</div>`) : ''}`;
    }

    const blocks = [];
    const productBlock = products.length
      ? group('Düfte', result.products.length, `<div class="pps__list" role="listbox">${products.map(productRow).join('')}</div>`)
      : '';
    const articleBlock = articles.length
      ? group('Aus dem Parfüm-Blog', result.articles.length, `<div class="pps__list" role="listbox">${articles.map(articleRow).join('')}</div>`)
      : '';
    if (this.preferJournal) blocks.push(articleBlock, productBlock);
    else blocks.push(productBlock, articleBlock);

    if (categories.length) {
      blocks.push(
        group(
          'Kategorien',
          null,
          chips(categories.map((c) => `<a class="pps__chip" href="${escapeHtml(c.u)}">${escapeHtml(c.n)} <span>${c.cnt}</span></a>`))
        )
      );
    }

    const note = result.partial
      ? `<p class="pps__hint">Keine exakten Treffer – das kommt deiner Suche am nächsten:</p>`
      : '';
    const allUrl = `${SEARCH_URL}?q=${encodeURIComponent(query)}${this.preferJournal ? '&from=journal' : ''}`;
    const footer = total
      ? `<a class="pps__all" href="${allUrl}">Alle ${total} Ergebnisse anzeigen <span aria-hidden="true">→</span></a>`
      : '';
    return note + blocks.join('') + footer;
  }

  renderError() {
    this.panel.innerHTML = `<div class="pps__none"><p class="pps__none-text">Die Suche lädt gerade nicht. Drück Enter, um auf der Ergebnisseite zu suchen.</p></div>`;
    if (this.mode === 'inline') this.panel.hidden = false;
  }

  closeInline() {
    if (this.mode !== 'inline') return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  onPanelClick(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-pps-term]') : null;
    if (!target) return;
    event.preventDefault();
    // The panel is re-rendered right away, which detaches the clicked chip; without this the
    // dialog / the inline dropdown would treat the click as "outside" and close.
    event.stopPropagation();
    this.input.value = target.getAttribute('data-pps-term') || '';
    this.input.focus();
    this.update(true);
  }

  onKeyDown(event) {
    const items = [...this.panel.querySelectorAll('.pps__row, .pps__chip, .pps__all')];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!items.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = ((this.activeIndex ?? -1) + step + items.length) % items.length;
      items.forEach((item, i) => item.classList.toggle('is-active', i === this.activeIndex));
      items[this.activeIndex].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.activeIndex >= 0 && items[this.activeIndex]) {
      event.preventDefault();
      /** @type {HTMLElement} */ (items[this.activeIndex]).click();
    } else if (event.key === 'Escape' && this.mode === 'inline') {
      this.closeInline();
    }
  }
}

if (!customElements.get('pp-search')) customElements.define('pp-search', PPSearch);

// Warm the index as soon as a shopper reaches for the search button.
const prefetch = (event) => {
  if (event.target instanceof Element && event.target.closest('search-button')) loadIndex().catch(() => {});
};
document.addEventListener('pointerover', prefetch, { passive: true });
document.addEventListener('touchstart', prefetch, { passive: true });

/* -------------------------------------------------- <pp-search-page> */

/**
 * /search: every product and article card is rendered by Liquid (so cards keep
 * quick-add); this element ranks them with the same search as the modal, hides
 * the rest, and keeps the counts, tabs and URL in sync while typing.
 */
class PPSearchPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.input = this.querySelector('[data-srch-input]');
    this.titleEl = this.querySelector('[data-srch-title]');
    this.status = this.querySelector('[data-srch-status]');
    this.cats = this.querySelector('[data-srch-cats]');
    this.empty = this.querySelector('[data-srch-empty]');
    this.groups = {
      products: this.querySelector('[data-srch-group="products"]'),
      articles: this.querySelector('[data-srch-group="articles"]'),
    };
    this.tab = 'all';
    const params = new URLSearchParams(location.search);
    this.preferJournal = params.get('from') === 'journal' || params.get('type') === 'article';
    if (this.preferJournal && this.groups.articles && this.groups.products) {
      this.groups.products.before(this.groups.articles);
      this.querySelector('[data-srch-tab="articles"]')?.after(this.querySelector('[data-srch-tab="products"]'));
    }

    this.querySelectorAll('[data-srch-tab]').forEach((button) =>
      button.addEventListener('click', () => {
        this.tab = button.getAttribute('data-srch-tab') || 'all';
        this.applyTab();
      })
    );
    this.addEventListener('click', (event) => {
      const chip = event.target instanceof Element ? event.target.closest('[data-pps-term]') : null;
      if (!chip || !this.input) return;
      event.preventDefault();
      this.input.value = chip.getAttribute('data-pps-term') || '';
      this.run();
    });
    let timer = 0;
    this.input?.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.run(), 150);
    });
    this.querySelector('form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.run();
      this.input?.blur();
    });
    this.run();
  }

  async run() {
    let index;
    try {
      index = await loadIndex();
    } catch {
      return; // keep Shopify's server-side results
    }
    const query = (this.input?.value || '').trim();
    const result = search(index, query);
    const counts = {
      products: this.place(this.groups.products, result.products.map((p) => p.key)),
      articles: this.place(this.groups.articles, result.articles.map((a) => a.key)),
    };
    const total = counts.products + counts.articles;

    if (this.titleEl) this.titleEl.textContent = query ? `Ergebnisse für „${query}“` : 'Wonach suchst du?';
    if (this.status) {
      this.status.textContent = !query
        ? 'Alle Düfte und Artikel – oder gib oben einen Suchbegriff ein.'
        : total
          ? `${total} ${total === 1 ? 'Ergebnis' : 'Ergebnisse'}${result.partial ? ' – keine exakten Treffer, das kommt deiner Suche am nächsten' : ''}`
          : '';
    }
    for (const [key, count] of Object.entries(counts)) {
      this.querySelectorAll(`[data-srch-count="${key}"]`).forEach((el) => (el.textContent = String(count)));
    }
    this.querySelectorAll('[data-srch-count="all"]').forEach((el) => (el.textContent = String(total)));
    if (this.cats) {
      this.cats.innerHTML = result.collections.length
        ? `<span class="srch__cats-label">Passende Kategorien</span>${result.collections
            .slice(0, 6)
            .map((c) => `<a class="pps__chip" href="${escapeHtml(c.u)}">${escapeHtml(c.n)} <span>${c.cnt}</span></a>`)
            .join('')}`
        : '';
      this.cats.hidden = !result.collections.length;
    }
    if (this.empty) {
      this.empty.hidden = total > 0;
      const term = this.empty.querySelector('[data-srch-term]');
      if (term) term.textContent = query;
    }
    this.counts = counts;
    this.applyTab();

    const url = new URL(location.href);
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    history.replaceState(history.state, '', url);
  }

  /** Shows the ranked cards in order, hides the rest; returns how many are shown. */
  place(groupEl, keys) {
    if (!groupEl) return 0;
    const list = groupEl.querySelector('[data-srch-list]');
    if (!list) return 0;
    const cards = new Map([...list.children].map((li) => [li.getAttribute('data-sid'), li]));
    let shown = 0;
    for (const key of keys) {
      const li = cards.get(key);
      if (!li) continue;
      list.appendChild(li);
      li.hidden = false;
      cards.delete(key);
      shown++;
    }
    for (const li of cards.values()) {
      li.hidden = true;
      list.appendChild(li);
    }
    return shown;
  }

  applyTab() {
    this.querySelectorAll('[data-srch-tab]').forEach((button) => {
      const on = button.getAttribute('data-srch-tab') === this.tab;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
    });
    for (const [key, el] of Object.entries(this.groups)) {
      if (!el) continue;
      const count = this.counts ? this.counts[key] : 1;
      el.hidden = !count || (this.tab !== 'all' && this.tab !== key);
    }
  }
}

if (!customElements.get('pp-search-page')) customElements.define('pp-search-page', PPSearchPage);
